/**
 * JumpingState - Player is ascending from a jump
 */

import BaseState from './BaseState';
import Phaser from 'phaser';
import eventBus, { GameEvents } from '../../core/EventBus';

export default class JumpingState extends BaseState {
    constructor() {
        super();
        this.maxSpeed = 13;
        this.acceleration = 0.012;
        this.angularSpeed = 0.18;
        this.jumpForce = -14;
        this.doubleJumpForce = -12;
        this.hasDoubleJumped = false;
    }

    enter(data) {
        // Perform the jump
        if (data?.isDoubleJump) {
            this.performDoubleJump();
        } else {
            this.performJump();
        }
        this.hasDoubleJumped = data?.isDoubleJump || false;
    }

    performJump() {
        this.sprite.setVelocityY(this.jumpForce);
        this.player.jumpCount = 1;
        this.player.isGrounded = false;
        this.player.applySquash(0.8, 1.2);
        this.player.createJumpParticles();
        
        // Emit event for audio/UI
        eventBus.emit(GameEvents.PLAYER_JUMP);
    }

    performDoubleJump() {
        this.sprite.setVelocityY(this.doubleJumpForce);
        this.player.jumpCount = 2;
        this.player.applySquash(0.7, 1.3);
        this.player.createDoubleJumpParticles();
        
        // Emit event for audio/UI
        eventBus.emit(GameEvents.PLAYER_DOUBLE_JUMP);
    }

    update(delta) {
        if (!this.player || this.player.isDead) return;

        const { cursors, wasd } = this.player;
        const velocity = this.sprite.body.velocity;

        // Air control (horizontal movement)
        const moveLeft = cursors.left.isDown || wasd.left.isDown;
        const moveRight = cursors.right.isDown || wasd.right.isDown;

        // Dash (if active, skip normal movement but keep magnetic pull)
        if (this.player.handleDash(moveLeft, moveRight)) {
            this.player.handleMagneticPull();
            this.applyVisualStretch(velocity);
            return;
        }

        if (moveLeft && velocity.x > -this.maxSpeed) {
            this.sprite.setAngularVelocity(-this.angularSpeed);
            this.sprite.applyForce({ x: -this.acceleration, y: 0 });
        } else if (moveRight && velocity.x < this.maxSpeed) {
            this.sprite.setAngularVelocity(this.angularSpeed);
            this.sprite.applyForce({ x: this.acceleration, y: 0 });
        }

        // Check for double jump input
        if (!this.hasDoubleJumped && this.player.jumpCount === 1) {
            if (this.player.checkJumpInputJustPressed()) {
                this.hasDoubleJumped = true;
                this.performDoubleJump();
            }
        }

        // Transition to falling when velocity becomes positive (going down)
        if (velocity.y > 0) {
            this.transitionTo('falling', { hasDoubleJumped: this.hasDoubleJumped });
            return;
        }

        // If somehow landed while still "jumping", go to appropriate state
        if (this.player.isGrounded && velocity.y >= 0) {
            this.transitionTo(moveLeft || moveRight ? 'rolling' : 'idle');
            return;
        }

        // Handle magnetic pull in air
        this.player.handleMagneticPull();

        // Visual stretch
        this.applyVisualStretch(velocity);
    }

    applyVisualStretch(velocity) {
        const stretchY = 1 + (velocity.y * 0.01);
        const stretchX = 1 - (velocity.y * 0.005);
        
        this.sprite.setScale(
            Phaser.Math.Clamp(stretchX * this.player.baseScaleX, 0.7, 1.3),
            Phaser.Math.Clamp(stretchY * this.player.baseScaleY, 0.7, 1.3)
        );
        
        if (this.player.highlight) {
            this.player.highlight.setScale(this.sprite.scaleX, this.sprite.scaleY);
        }
    }

    exit() {
        // Nothing specific
    }
}
